'use client';

/**
 * On mount: reads the user's preferred language from the Django JWT (accessToken),
 * compares it to the current NEXT_LOCALE cookie, and sets the cookie + refreshes
 * if they differ. This ensures the user's saved language preference loads on login.
 */

import { useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';

function decodeLanguage(accessToken: string): string {
  try {
    const payload = JSON.parse(Buffer.from(accessToken.split('.')[1], 'base64').toString());
    return payload.language ?? 'en';
  } catch {
    return 'en';
  }
}

export function LocaleSync() {
  const { data: session } = useSession();
  const currentLocale = useLocale();
  const router = useRouter();

  useEffect(() => {
    if (!session) return;
    const accessToken = (session as { accessToken?: string }).accessToken;
    if (!accessToken) return;

    const userLanguage = decodeLanguage(accessToken);
    if (userLanguage !== currentLocale) {
      // Set cookie to match user's saved preference then refresh
      fetch('/api/set-locale', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale: userLanguage }),
      }).then(() => router.refresh());
    }
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
